import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { sortBy } from 'lodash-es'
import { Add } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, roofStoreyKey } from '@editorStores'
import { postCreatePositionPlan } from '@mutations'
import ExportMultiSelectItem from 'src/components/pages/Editor/pages/Export/components/ExportMultiSelectItem'
import { storeyToLabel } from '../utils'

interface Props {
  params: Record<string, string | number | boolean>
  'data-cy'?: string
}

const CombinedPositionPlanItem = ({ params }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const { projectId } = useParams()
  const queryClient = useQueryClient()

  const availableStoreys = useModelStore(state => state.availableStoreys)

  const sortedStoreys = useMemo(() => sortBy([...availableStoreys]), [availableStoreys])

  const availableStoreysOptions = useMemo(
    () =>
      sortedStoreys.map(storey => ({
        label: storeyToLabel(storey),
        value: storey,
      })),
    [sortedStoreys],
  )

  const [storeys, setStoreys] = useState([roofStoreyKey])

  const { isLoading, mutate } = useMutation(
    (storeys: Array<string>) => {
      // Call the new request
      return postCreatePositionPlan.request(projectId as string, storeys, params)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getPlans', projectId])
        enqueueSnackbar('Plan wurde erfolgreich erstellt', { variant: 'success' })
      },
      onError: (e: Error) => {
        enqueueSnackbar(buildErrorMessage(e, 'Fehler beim Erstellen der Pläne'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <ExportMultiSelectItem
      title="Dach + weitere"
      isLoading={isLoading}
      onClick={() => mutate(storeys)}
      options={availableStoreysOptions}
      selectedOptions={storeys}
      handleChange={event => {
        const { value } = event.target
        setStoreys(typeof value === 'string' ? value.split(',') : value)
      }}
      icon={<Add fontSize="small" />}
    />
  )
}

export default CombinedPositionPlanItem
